import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader, CheckCircle, XCircle } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../hooks/useAuth';
import analytics from '../lib/tracking';
import { endLoginJourney } from '../lib/telemetry';

export default function GoogleCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleGoogleCallback } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const hasProcessed = useRef(false);

  useEffect(() => {
    const processCallback = async () => {
      // Prevent double-processing in React StrictMode or on re-renders
      if (hasProcessed.current) {
        return;
      }
      hasProcessed.current = true;
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Strip the OAuth params from the URL so a reload or back-navigation
      // can't replay an already-consumed code (Cognito returns invalid_grant
      // on reuse). We do this before any await so the cleanup happens whether
      // the exchange succeeds or fails.
      window.history.replaceState({}, '', '/auth/google/callback');

      // Handle OAuth errors
      if (error) {
        console.error('OAuth error:', error, errorDescription);
        setStatus('error');
        setErrorMessage(errorDescription || 'Authentication failed');

        analytics.error('google_oauth_error', error);

        Sentry.captureMessage(`Google OAuth error: ${error}`, {
          level: 'error',
          tags: { error_type: 'google_oauth_error' },
          extra: { error, error_description: errorDescription }
        });
        return;
      }

      // No authorization code - redirect to home
      if (!code) {
        console.error('No authorization code received');
        navigate('/', { replace: true });
        return;
      }

      try {
        // Retrieve UTM params from sessionStorage if they exist
        let utmParams = undefined;
        const utmParamsStr = sessionStorage.getItem('utm_params');
        if (utmParamsStr) {
          try {
            utmParams = JSON.parse(utmParamsStr);
            console.log('User signed up with UTM params:', utmParams);
            sessionStorage.removeItem('utm_params');
          } catch (e) {
            console.error('Failed to parse UTM params:', e);
          }
        }

        // Exchange code for tokens, passing UTM params if available
        await handleGoogleCallback(code, utmParams);

        setStatus('success');
        endLoginJourney('success');

        // Track successful authentication
        analytics.conversions.userAuthenticated({
          method: 'google_oauth'
        });

        // Redirect to home after a brief success message
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      } catch (error) {
        console.error('Google OAuth callback failed:', error);
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Authentication failed');

        endLoginJourney(
          'error',
          {},
          error instanceof Error ? error : new Error(String(error))
        );

        analytics.error('google_oauth_callback_failed', error instanceof Error ? error.message : 'Unknown error');

        Sentry.captureException(error, {
          level: 'error',
          tags: { error_type: 'google_oauth_callback_failed' }
        });
      }
    };

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount - ref prevents double-processing

  const getIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader className="w-16 h-16 text-primary-pink animate-spin" />;
      case 'success':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'error':
        return <XCircle className="w-16 h-16 text-red-500" />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'loading':
        return 'Signing you in...';
      case 'success':
        return 'Welcome!';
      case 'error':
        return 'Sign In Failed';
    }
  };

  const getMessage = () => {
    switch (status) {
      case 'loading':
        return 'Please wait while we complete your sign in with Google.';
      case 'success':
        return 'Successfully signed in! Redirecting...';
      case 'error':
        return errorMessage || 'Something went wrong. Please try again.';
    }
  };

  return (
    <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-auto border-2 border-accent-pink text-center">
        <div className="mb-6 flex justify-center">
          {getIcon()}
        </div>

        <h2 className="text-text-dark text-2xl font-semibold mb-4">
          {getTitle()}
        </h2>

        <p className="text-text-medium text-sm leading-relaxed mb-6">
          {getMessage()}
        </p>

        {status === 'error' && (
          <button
            onClick={() => navigate('/signup')}
            className="w-full bg-primary-pink hover:bg-pink-400 text-white font-medium py-3 px-6 rounded-xl transition-colors"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  );
}
